import { io } from 'socket.io-client'
import serverUrl from '@/assets/BaseServerUrl.js'

class WebSocketService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.onlineCount = 0
    this.userInfo = {
      ip: '未知IP',
      region: '未知地区',
      isp: '未知ISP'
    }
    this.callbacks = {
      onlineCount: [],
      userInfo: [],
      connected: [],
      disconnected: [],
      privateMessage: [],
      serverMessage: [],
      userOnline: [],
      userOffline: [],
      officialNotification: [],
      messagesRead: [],
      translateProgress: [],
      translateCompleted: [],
      translateFailed: []
    }
  }

  // 检查是否为网络爬虫或自动化无头浏览器
  isCrawlerEnvironment() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return true
    }
    const ua = (navigator.userAgent || '').toLowerCase()
    const crawlerPattern = /bot|spider|crawler|facebookexternalhit|facebot|meta-externalagent|headless|phantom|selenium|puppeteer|playwright/i
    if (crawlerPattern.test(ua)) {
      return true
    }
    if (navigator.webdriver === true || window.__nightmare || window._phantom || window.callPhantom) {
      return true
    }
    return false
  }

  // 连接WebSocket
  connect() {
    // 爬虫或自动化预览机器人直接跳过连接，防止伪造在线与游客记录
    if (this.isCrawlerEnvironment()) {
      return
    }

    if (this.socket && this.isConnected) {
      return
    }

    try {
      const userToken = localStorage.getItem('userToken') || ''
      this.socket = io(serverUrl, {
        auth: { userToken },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      })

      this.setupEventListeners()
    } catch (error) {
      console.error('WebSocket连接失败:', error)
    }
  }

  // 设置事件监听器
  setupEventListeners() {
    this.socket.on('connect', () => {
      console.log('WebSocket连接成功')
      this.isConnected = true
      this.triggerCallbacks('connected')
      this.registerUser()
    })

    this.socket.on('disconnect', () => {
      console.log('WebSocket连接断开')
      this.isConnected = false
      this.triggerCallbacks('disconnected')
    })

    this.socket.on('userCount', (count) => {
      this.onlineCount = count
      this.triggerCallbacks('onlineCount', count)
    })

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket连接错误:', error)
    })

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`WebSocket重连成功，尝试次数: ${attemptNumber}`)
      this.registerUser()
    })

    // 私聊消息事件
    this.socket.on('privateMessage', (message) => {
      this.triggerCallbacks('privateMessage', message)
    })

    // 私聊撤回事件
    this.socket.on('privateMessageRecalled', (data) => {
      this.triggerCallbacks('privateMessageRecalled', data)
    })

    // 服务器广播消息
    this.socket.on('serverMessage', (data) => {
      this.triggerCallbacks('serverMessage', data)
    })

    // 用户上线事件
    this.socket.on('userOnline', (data) => {
      this.triggerCallbacks('userOnline', data)
    })

    // 用户下线事件
    this.socket.on('userOffline', (data) => {
      this.triggerCallbacks('userOffline', data)
    })

    // 官方通知事件
    this.socket.on('officialNotification', (data) => {
      this.triggerCallbacks('officialNotification', data)
    })

    // 消息已读回执事件
    this.socket.on('messagesRead', (data) => {
      this.triggerCallbacks('messagesRead', data)
    })

    // 后台 AI 翻译进度与结果（服务端只定向推给任务归属用户）
    this.socket.on('translate_progress', (data) => {
      this.triggerCallbacks('translateProgress', data)
    })

    this.socket.on('translate_completed', (data) => {
      this.triggerCallbacks('translateCompleted', data)
    })

    this.socket.on('translate_failed', (data) => {
      this.triggerCallbacks('translateFailed', data)
    })
  }

  // 注册用户
  async registerUser() {
    if (this.isCrawlerEnvironment()) {
      return
    }
    try {
      // 获取IP信息
      const ipInfo = await this.getIpInfo()

      // 获取用户信息
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const username = userInfo.username || '匿名用户'

      // 获取userId：已登录用户使用账号ID，未登录用户使用游客ID
      let userId
      if (userInfo.userId) {
        // 已登录用户，使用账号的真实ID
        userId = userInfo.userId
      } else {
        // 未登录用户，使用本地缓存的游客ID或生成新的游客ID
        userId = localStorage.getItem('guestUserId') || sessionStorage.getItem('guestUserId')
        if (!userId) {
          userId = this.generateUserId()
          // 优先存储到localStorage，确保跨会话保持一致
          localStorage.setItem('guestUserId', userId)
          sessionStorage.setItem('guestUserId', userId)
        }
      }

      // 注册到服务器
      this.socket.emit('register', {
        userId: userId,
        username: username,
        ipInfo: ipInfo
      })

      // 更新本地用户信息
      this.userInfo = {
        ip: ipInfo.ip || '未知IP',
        region: ipInfo.region || '未知地区',
        isp: ipInfo.isp || '未知ISP'
      }

      this.triggerCallbacks('userInfo', this.userInfo)

      // 启动心跳
      this.startHeartbeat(ipInfo)
    } catch (error) {
      console.error('用户注册失败:', error)
    }
  }

  // 获取IP信息
  async getIpInfo() {
    // IP获取API列表，按优先级排序
    // const ipApis = [
    //   {
    //     url: 'https://ip.rainview.net/api/ip',
    //     parser: (data) => data.ip
    //   },
    //   {
    //     url: 'https://api.ipify.org?format=json',
    //     parser: (data) => data.ip
    //   },
    //   {
    //     url: 'http://httpbin.org/ip',
    //     parser: (data) => data.origin
    //   },
    //   {
    //     url: 'https://cdid.c-ctrip.com/model-poc2/h',
    //     parser: (data) => typeof data === 'string' ? data.trim() : data
    //   },
    //   {
    //     url: 'https://vv.video.qq.com/checktime?otype=ojson',
    //     parser: (data) => data.ip
    //   },
    //   {
    //     url: 'https://test.ipw.cn/api/ip/myip?json',
    //     parser: (data) => data.IP
    //   },
    //   {
    //     url: 'https://ipv4.my.ipinfo.app/api/ipDetails.php',
    //     parser: (data) => data.ip
    //   },
    //   {
    //     url: 'https://g3.letv.com/r?format=1',
    //     parser: (data) => data.host || data.remote
    //   }
    // ]

    let userIp = '未知IP'

    // 尝试从多个API获取IP地址
    try {
      console.log(`正在获取IP地址...`)
      const ipResponse = await fetch(`${serverUrl}/get-ip`, {
        timeout: 5000 // 5秒超时
      })

      if (ipResponse.ok) {
        const responseText = await ipResponse.text()
        let ipData

        try {
          // 尝试解析为JSON
          ipData = JSON.parse(responseText)
        } catch {
          // 如果不是JSON，直接使用文本
          ipData = responseText
        }

        userIp = ipData.ip || ipData
        if (userIp && userIp !== '未知IP') {
          console.log(`成功获取到IP: ${userIp}`)
        }
      }
    } catch (error) {
      console.warn(`获取IP失败:`, error.message)
    }

    try {
      // 使用后端的IP查询接口获取归属地信息
      const response = await fetch(`${serverUrl}/get-ip-info?ip=${userIp}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success && result.data && result.data.ipdata) {
          const ipdata = result.data.ipdata
          return {
            ip: ipdata.text || userIp,
            region: `${ipdata.info1 || ''}${ipdata.info2 || ''}`,
            isp: ipdata.isp || '未知ISP',
            data: result.data
          }
        } else {
          // 如果后端查询失败，至少返回IP地址
          return {
            ip: userIp,
            region: '未知地区',
            isp: '未知ISP'
          }
        }
      } else {
        // 如果后端查询失败，至少返回IP地址
        return {
          ip: userIp,
          region: '未知地区',
          isp: '未知ISP'
        }
      }
    } catch (error) {
      console.error('获取IP信息失败:', error)
      return {
        ip: userIp, // 即使后端查询失败，也返回获取到的IP
        region: '未知地区',
        isp: '未知ISP'
      }
    }
  }

  // 生成用户ID
  generateUserId() {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 15)
    return `user_${timestamp}_${random}`
  }

  // 启动心跳
  startHeartbeat(ipInfo) {
    // 启动前先清理已有的心跳定时器，防重复注册泄露
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
    // 每30秒发送一次心跳
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected) {
        // 获取当前用户ID
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        let userId
        if (userInfo.userId) {
          userId = userInfo.userId
        } else {
          userId = localStorage.getItem('guestUserId') || sessionStorage.getItem('guestUserId')
        }

        this.socket.emit('heartbeat', {
          userId: userId,
          ipInfo: ipInfo
        })
      }
    }, 30000)
  }

  // 断开连接
  disconnect() {
    // 清理心跳定时器
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
    }
  }

  // 添加回调函数
  on(event, callback) {
    if (this.callbacks[event]) {
      const list = this.callbacks[event]
      // 防止重复注册同一回调，避免事件触发多次
      if (!list.includes(callback)) {
        list.push(callback)
      }
    }
  }

  // 移除回调函数
  off(event, callback) {
    if (this.callbacks[event]) {
      const index = this.callbacks[event].indexOf(callback)
      if (index > -1) {
        this.callbacks[event].splice(index, 1)
      }
    }
  }

  // 触发回调函数
  triggerCallbacks(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event].forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`回调函数执行错误 (${event}):`, error)
        }
      })
    }
  }

  // 获取当前在线人数
  getOnlineCount() {
    return this.onlineCount
  }

  // 获取用户信息
  getUserInfo() {
    return this.userInfo
  }

  // 检查连接状态
  isSocketConnected() {
    return this.isConnected
  }
}

// 创建单例实例
const websocketService = new WebSocketService()

export default websocketService